import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { CommitteeApprovalType } from '../committee-approval-type';
import {
  ClosingApprovalStatus,
  CommitteeApproval,
  CommitteeApprovalStatus,
} from '../committee-approval';
import queries from './graphql/committee-approval';
import moment from 'moment';
import { Apollo } from 'apollo-angular';
import { Observable, Subscription } from 'rxjs';
import { CurrentUserService } from '../../../services/current-user.service';
import { CommitteeItem, isRelatedToApprovalType } from '../../committee-items/committee-item';
import { CommitteeItemsService } from '../../committee-items/committee-items.service';
import { UserPermissionsService } from 'src/app/services/user-permissions.service';
import { User } from 'src/app/interfaces/user';
import { CommitteeApprovalsService } from '../committee-approvals.service';

@Component({
    selector: 'app-committee-approval',
    templateUrl: './committee-approval.component.html',
    styleUrls: ['./committee-approval.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CommitteeApprovalComponent implements OnInit {
  @Input('approval') approval: CommitteeApproval | null;
  @Input('approvalType') approvalType: CommitteeApprovalType;
  @Input() approvalTypes?: CommitteeApprovalType[] = [];
  @Input('isMilestoneMember') isMilestoneMember: boolean;
  @Input('projectId') projectId: string;
  @Input('milestoneId') milestoneId: string;
  @Input('milestoneName') milestoneName: string;
  @Input() readOnly: boolean;

  public statusKeys: Array<string> = Object.keys(CommitteeApprovalStatus);
  public statuses = CommitteeApprovalStatus;
  public showItems: boolean = false;
  public items: CommitteeItem[] = [];
  private itemsSubscription: Subscription;
  private userSub: Subscription;
  public user: User;
  public headerColumns: Array<string> = []

  @Output('approvalChange') approvalChange =
    new EventEmitter<CommitteeApproval>();
  constructor(
    public apollo: Apollo,
    public currentUser: CurrentUserService,
    public itemsService: CommitteeItemsService,
    public permsService: UserPermissionsService,
    public approvalService: CommitteeApprovalsService
  ) {}

  ngOnInit(): void {
    this.headerColumns = [
      'cleared',
      'issue',
      'solution',
      'post_closing',
      'added_by',
      'added_on',
      'updated_by',
      'updated_on',
      'actions'
    ]
    if(this.approvalType.name?.toLowerCase() === 'executive' || this.approvalType.name?.toLowerCase() == 'refresh'){
      this.headerColumns = [
        'cleared',
        'issue',
        'solution',
        'post_closing',
        'related_department',
        'added_by',
        'added_on',
        'updated_by',
        'updated_on',
        'actions'
      ]
    }
    if(this.milestoneName.toLowerCase() == 'closed'){
      this.statuses = ClosingApprovalStatus
      this.statusKeys = Object.keys(ClosingApprovalStatus)
      this.headerColumns.splice(3, 1)
    }

    if (this.approval === null) {
      this.approval = {
        status: null,
        __typename: 'CommitteeApproval',
      };
    }
    this.currentUser.getUser().subscribe((user) => (this.user = user));
    if (this.itemsService.committeeItems$[this.projectId]) {
      this.itemsSubscription = this.itemsService.committeeItems$[
        this.projectId
      ].subscribe((items) => {
        if (this.approvalType?.id && items) {
          let executive = this.approvalTypes?.find(type => type.name?.toLowerCase() === 'executive');
          let refresh = this.approvalTypes?.find(type => type.name?.toLowerCase() === 'refresh');
          this.items = items.filter(
            (item) => item.committee_approval_type_id === this.approvalType.id
            || ((item.committee_approval_type_id === executive?.id || item.committee_approval_type_id === refresh?.id)
              && isRelatedToApprovalType(item, this.approvalType.id))
          );
        }
      });
    }
  }
  ngOnDestroy() {
    this.itemsSubscription && this.itemsSubscription.unsubscribe();
    this.userSub && this.userSub.unsubscribe();
  }
  getIconName(): string {
    let icon = 'help_outline';
    if (this.isMilestoneMember && this.approval?.status) {
      switch (this.approval?.status) {
        case CommitteeApprovalStatus['Cleared']:
          icon = 'check_circle';
          break;
        case ClosingApprovalStatus['Approve Fund & Release']:
          icon = 'check_circle'
          break;
        case CommitteeApprovalStatus['Cleared with Disclosures']:
          icon = 'error';
          break;
        case ClosingApprovalStatus['Approve Fund Only']:
          icon = 'error'
          break;
        case CommitteeApprovalStatus['Requires Discussion']:
          icon = 'chat_bubble';
          break;
        case CommitteeApprovalStatus['Not Cleared']:
          icon = 'cancel';
          break;
        case ClosingApprovalStatus['Not Approved']:
          icon = 'cancel'
          break;
      }
    }
    return icon;
  }
  getColorClass(): string {
    let colorClass = 'unknown';
    if (this.isMilestoneMember && this.approval?.status) {
      switch (this.approval?.status) {
        case CommitteeApprovalStatus['Cleared']:
          colorClass = 'yes';
          break;
        case ClosingApprovalStatus['Approve Fund & Release']:
          colorClass = 'yes'
          break;
        case CommitteeApprovalStatus['Cleared with Disclosures']:
          colorClass = 'maybe';
          break;
        case ClosingApprovalStatus['Approve Fund Only']:
          colorClass = 'maybe';
          break;
        case CommitteeApprovalStatus['Not Cleared']:
          colorClass = 'no';
          break;
        case ClosingApprovalStatus['Not Approved']:
          colorClass = 'no';
          break;
        case CommitteeApprovalStatus['Requires Discussion']:
          colorClass = 'discuss';
      }
    }
    return colorClass;
  }
  showItemsClass(): string {
    return this.showItems ? 'expand' : 'collapse';
  }
  getDateString(): string {
    if (this.approval && this.approval?.hasOwnProperty('id')) {
      try {
        let date =
          this.approval.modified &&
          this.approval.modified !== this.approval.created
            ? moment(this.approval.modified)
            : moment(this.approval.created);
        return date.format('MM/DD/YYYY');
      } catch (e) {
        return '';
      }
    }
    return '';
  }
  onSelectApproval(status: string) {
    this.handleUpdateApproval({ __typename: 'CommitteeApproval', status });
    let observable: Observable<any>;
    if (this.approval?.hasOwnProperty('id')) {
      observable = this.updateApproval();
    } else {
      observable = this.createApproval();
    }
    let sub = observable.subscribe(({ data }) => {
      if (data.hasOwnProperty('approval')) {
        this.handleUpdateApproval(data.approval);
      }
      sub && sub.unsubscribe();
    });
    return true;
  }

  shouldShow(status: any, items: any[]) {
    if(this.milestoneName.toLowerCase()==='closed' && status.toLowerCase() === 'approve fund & release'){
      if(items.length === 0){
        return true;
      }
      return items.every((item:any) => item.cleared);
    }
    if (status.toLowerCase() !== 'cleared' || this.approval?.status?.toLowerCase() === 'approved') {
      return true;
    } else {
      if (items.length === 0) {
        return true;
      }
      for (let i = 0; i < items.length; i += 1) {
        if (
          !(
            items[i].cleared === true ||
            items[i].is_legal_note === true ||
            (items[i].cleared !== true && items[i].is_post_closing === true)
          )
        ) {
          return false;
        }
      }
    }
    return true;
  }

  getStatusForRequest() {
    return this.approval?.status?.length && this.approval.status !== 'null'
      ? this.approval.status
      : null;
  }
  createApproval(): Observable<any> {
    return this.apollo.mutate({
      mutation: queries.CREATE_APPROVAL,
      variables: {
        input: {
          // filter empty string or "null" to null
          status: this.getStatusForRequest(),
          committee_approval_type_id: this.approvalType.id,
          user_id: this.user.id,
          milestone_id: this.milestoneId,
          project_id: this.projectId,
        },
      },
    });
  }
  updateApproval(): Observable<any> {
    return this.apollo.mutate({
      mutation: queries.UPDATE_APPROVAL,
      variables: {
        input: {
          //filter empty string or "null" to null
          status: this.getStatusForRequest(),
          committee_approval_type_id: this.approvalType.id,
          user_id: this.user.id,
        },
        id: this.approval?.id,
      },
    });
  }
  filteredItems(): CommitteeItem[] {
    return this.items.filter((item) => !item.is_legal_note);
  }
  handleUpdateApproval(approval: CommitteeApproval): void {
    this.approval = { ...this.approval, ...approval };
    this.approvalChange.emit(this.approval);
  }
  addItem(e: Event) {
    e.preventDefault();
    this.showItems = true;
    if (this.approvalType?.id) {
      this.itemsService.addItem(this.projectId, this.approvalType.id);
    }
  }
}
